/** Weapon forgemaging preview (neutral damage / vol / heal → one element, floored). Matches backend gen rules. */

import { ELEMENTS, HEAL_EFFECT_IDS } from './weapon';

// The fraction of each bound a forgemage keeps — `WEAPON_MAGING_DAMAGE_MULT`
// in `gen/engine/spell_scoring.py`, which is what the solvers actually score.
// The beta moved it from 0.85 to 1.0; change it in both places or the card
// shows dice the build's own total disagrees with.
const MAGING_DAMAGE_MULT = 1.0;

// Also the order the solvers index forgemage choices by — this list is
// `spell_scoring.MAGING_ELEMENT_ORDER`, and `magingElementForChoice` below
// turns a solve's answer back into a key. Reordering it here alone renders a
// fire-maged weapon as an earth one.
const MAGING_ELEMENT_KEYS = ['earth', 'fire', 'water', 'air'];

const ELEMENT_KEY_TO_ID = {
    earth: 1,
    fire: 2,
    water: 3,
    air: 4,
};

// The languages an effect description carries. Each names Neutral in its own
// words ("Neutre", "Neutro", "neutral"), so each is rewritten in its own words.
const DESC_LANGS = ['fr', 'en', 'de', 'es', 'pt'];

const NEUTRAL_ELEMENT_ID = 0;

const hasDice = (ef) =>
    Number.isFinite(Number(ef?.min)) || Number.isFinite(Number(ef?.max));

const isHealEffect = (ef) => HEAL_EFFECT_IDS.has(Number(ef?.effect_id));

/**
 * The two halves a weapon is maged in, separately: `damage` covers the neutral
 * damage and vol rows, `heal` the neutral "soins" ones.
 *
 * They are separate because a player mages them apart on purpose — heal into
 * the element they stack so it lands on allies, damage into one they do not —
 * and one element for both would make that build unexpressible.
 */
const isMagableDamage = (ef) =>
    Number(ef?.element_id) === NEUTRAL_ELEMENT_ID && hasDice(ef) && !isHealEffect(ef);

const isMagableHeal = (ef) =>
    Number(ef?.element_id) === NEUTRAL_ELEMENT_ID && hasDice(ef) && isHealEffect(ef);

const scaleVal = (v) => {
    if (!Number.isFinite(Number(v))) return v;
    return Math.floor(Number(v) * MAGING_DAMAGE_MULT);
};

/** Rewrite localized effect desc so the element it names matches the maged one (icon follows element_id). */
const rewriteNeutralDesc = (desc, elKey) => {
    if (!desc || typeof desc !== 'object') return desc;
    const labels = ELEMENTS[ELEMENT_KEY_TO_ID[elKey]]?.label;
    const neutral = ELEMENTS[NEUTRAL_ELEMENT_ID]?.label;
    if (!labels || !neutral) return desc;
    const next = { ...desc };
    for (const lang of DESC_LANGS) {
        if (typeof next[lang] !== 'string' || !neutral[lang] || !labels[lang]) continue;
        next[lang] = next[lang].replace(
            new RegExp(`\\b${neutral[lang]}\\b`, 'gi'),
            // "vol Neutre" and "de robo neutral" each keep the case they had.
            (match) => (match === match.toLowerCase() ? labels[lang].toLowerCase() : labels[lang]),
        );
    }
    return next;
};

/**
 * @param {string|null|undefined} v
 * @returns {'earth'|'fire'|'water'|'air'|null}
 */
export function normalizeWeaponMagingElement(v) {
    if (v == null || v === '') return null;
    const s = String(v).trim().toLowerCase();
    return MAGING_ELEMENT_KEYS.includes(s) ? s : null;
}

/**
 * A whole forgemage choice, as `{ damage, heal }`.
 *
 * A bare element string is read as the damage half, which is what it meant
 * before heals could be maged apart — saved builds and solver payloads still
 * carry that shape.
 *
 * @param {string|{damage?: string, heal?: string}|null|undefined} v
 * @returns {{damage: string|null, heal: string|null}|null} null when neither half is set
 */
export function normalizeWeaponMaging(v) {
    if (v == null || v === '') return null;
    if (typeof v === 'string') {
        const damage = normalizeWeaponMagingElement(v);
        return damage ? { damage, heal: null } : null;
    }
    if (typeof v !== 'object') return null;
    const damage = normalizeWeaponMagingElement(v.damage);
    const heal = normalizeWeaponMagingElement(v.heal);
    return damage || heal ? { damage, heal } : null;
}

/**
 * The elemental *damage* characteristic each element is identified by on the
 * wire. Mirrors `_ELEM_PAIR` in `gen/engine/spell_scoring.py`.
 */
const MAGING_ELEMENT_BY_DAMAGE_ID = { 88: 'earth', 89: 'fire', 90: 'water', 91: 'air' };

/**
 * A solve's forgemage answer as an element key; 0 means "as forged".
 *
 * The find-the-best-weapon criterion picks the element while it scores, so the
 * card has to be told which one, or it renders a weapon the solver did not
 * score and shows a total that disagrees with the build's own score.
 *
 * Keyed on the damage characteristic rather than a position in the candidate's
 * variant list: that list holds only the elements the request scores, so a
 * water/air request puts water at index 0. Decoding the position against all
 * four elements is what rendered a water-maged bow as an earth-maged one.
 *
 * @param {number} damageId `weapon_maging_damage_id` from the kernel
 * @returns {'earth'|'fire'|'water'|'air'|null}
 */
export function magingElementForDamageId(damageId) {
    return MAGING_ELEMENT_BY_DAMAGE_ID[Number(damageId)] ?? null;
}

/**
 * True if weapon has at least one neutral (element_id 0) damage or vol line with
 * dice — a maging candidate for the damage half.
 * @param {object} weapon - `item.weapon`
 */
export function weaponHasNeutralDamageForMaging(weapon) {
    if (!weapon || !Array.isArray(weapon.effects)) return false;
    return weapon.effects.some(isMagableDamage);
}

/**
 * True if weapon has at least one neutral "soins" line with dice — a maging
 * candidate for the heal half.
 * @param {object} weapon - `item.weapon`
 */
export function weaponHasNeutralHealForMaging(weapon) {
    if (!weapon || !Array.isArray(weapon.effects)) return false;
    return weapon.effects.some(isMagableHeal);
}

/**
 * @param {object} weapon
 * @param {string|{damage?: string, heal?: string}|null} maging - element key(s), or null
 * @returns {object} shallow-cloned weapon with transformed effects, or original if no change
 */
export function applyWeaponMagingPreview(weapon, maging) {
    const choice = normalizeWeaponMaging(maging);
    if (!weapon || !choice || !Array.isArray(weapon.effects)) return weapon;
    const effects = weapon.effects.map((ef) => {
        const el = (isMagableHeal(ef) && choice.heal)
            || (isMagableDamage(ef) && choice.damage)
            || null;
        if (!el) return ef;
        const nextMin = Number.isFinite(Number(ef.min)) ? scaleVal(ef.min) : ef.min;
        const nextMax = Number.isFinite(Number(ef.max))
            ? scaleVal(ef.max)
            : (Number.isFinite(Number(ef.min)) ? scaleVal(ef.min) : ef.max);
        return {
            ...ef,
            element_id: ELEMENT_KEY_TO_ID[el],
            min: nextMin,
            max: nextMax,
            desc: rewriteNeutralDesc(ef.desc, el),
        };
    });
    return { ...weapon, effects };
}
